import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { Cmg1003LookupsQuery, Cmg1003LookupsStore } from '../../loan-state';
import { IdValue } from '../../shared/models/id-value.model';
import { CmgUiUtilities } from '../components/common/cmg-ui-utils';
import { AdditionalProdAssistants, LicensingAgencies, LoanReferralsModel, OriginationChannel } from '../models';
import { Cmg1003LookupsModel } from '../models/cmg-1003-lookups.model';
import { LoanProgram } from '../models/lookups/loan-program.model';
import { PricingLookupsModel } from '../models/lookups/pricing.lookups.model';
import { ZipcodeModel } from '../models/lookups/zip-code.model';
import { REQUIRES_SECURITY_PROFILE } from '../../interceptors';
import { CountiesModel } from '../models/lookups/counties.model';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';

@Injectable({ providedIn: 'root' })
export class Cmg1003LookupsService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _lookupsStore = inject(Cmg1003LookupsStore);
  private readonly _lookupsQuery = inject(Cmg1003LookupsQuery);
  private readonly _environment = inject(APP_ENV);

  private _zipcodeModels: Record<string, ZipcodeModel> = {};

  private readonly _getLookups = () => this._httpClient.get<Cmg1003LookupsModel>(`${this._environment.retail.apiUrl}/lookups`);

  private readonly _getCounties = (): Observable<IdValue<string>[]> => {
    const currentCounties = this._lookupsQuery.getValue().counties;
    if (currentCounties && currentCounties.length > 0) {
      return of(currentCounties);
    } else {
      const url = `${this._environment.retail.apiUrl}/lookups/counties`;
      return this._httpClient
        .get<CountiesModel[]>(url, {
          headers: CmgUiUtilities.getSkipSpinnerHeaders(),
        })
        .pipe(map((response) => response.map((county) => new IdValue(county.countyID.toString(), county.state, county.name))));
    }
  };

  private readonly _getJrloNames = (searchString?: string) =>
    this._httpClient.get<IdValue<string>[]>(`${this._environment.retail.apiUrl}/lookups/jrlonames` + (searchString ? `/${searchString}` : ''));

  private readonly _getAdditionalProdAssistants = () =>
    this._httpClient.get<AdditionalProdAssistants>(`${this._environment.retail.apiUrl}/lookups/additionalprodassistants`);

  readonly getRetailLeadTypes = () =>
    this._httpClient
      .get<IdValue<string>[]>(`${this._environment.retail.apiUrl}/lookups/leadtypes`, {
        context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
        headers: CmgUiUtilities.getSkipSpinnerHeaders(),
      })
      .pipe(
        map((r) => {
          // TODO: 16575 can remove this filter if removed from backend values
          // Removed Brokered Out from this filter in 23799.
          return r.filter((t) => t.name !== 'Builder - Brokered');
        }),
      );

  readonly getLoanPrograms = (channel?: OriginationChannel) =>
    this._httpClient
      .get<LoanProgram[]>(`${this._environment.retail.apiUrl}/lookups/loanprograms${channel ? '?originationchannel=' + channel : ''}`, {
        context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
        headers: CmgUiUtilities.getSkipSpinnerHeaders(),
      })
      .pipe(
        tap((loanPrograms) => {
          if (loanPrograms?.length > 0) {
            this._lookupsStore.update({ loanPrograms: loanPrograms });
          }
        }),
      );

  private readonly _getRetailCompPlans = () => this._httpClient.get<IdValue<string>[]>(`${this._environment.retail.apiUrl}/lookups/retailcompplans`);

  private readonly _getLoanStatusItems = () => this._httpClient.get<IdValue<string>[]>(`${this._environment.retail.apiUrl}/lookups/loanstatusitems`);

  private readonly _getLoanReferrals = () => this._httpClient.get<LoanReferralsModel[]>(`${this._environment.retail.apiUrl}/lookups/loanreferrals`);

  private readonly _getSecurityProfileRoles = () =>
    this._httpClient.get<IdValue<string>[]>(`${this._environment.retail.apiUrl}/lookups/securityprofileroles`);

  private readonly _getLicensingAgencies = () =>
    this._httpClient.get<LicensingAgencies[]>(`${this._environment.retail.apiUrl}/lookups/licensingagencies`);

  private readonly _getPricing = () => this._httpClient.get<PricingLookupsModel>(`${this._environment.retail.apiUrl}/lookups/pricing`);

  private readonly _getRelocationRefTypes = () =>
    this._httpClient.get<LoanReferralsModel[]>(`${this._environment.retail.apiUrl}/lookups/relocationreftypes`);

  getCounties(): Observable<IdValue<string>[]> {
    return this._lookupsQuery.select((state) => state.counties);
  }

  getCityStateByZipCode(zipCode: string, skipSpinner = false): Observable<ZipcodeModel> {
    return this._lookupsQuery
      .select((s) => s.zipCodeModels)
      .pipe(
        take(1),
        switchMap((zipCodeModels) => {
          if (zipCodeModels[zipCode]) {
            return of(zipCodeModels[zipCode]);
          } else {
            const zipcodeEndpoint = `${this._environment.retail.apiUrl}/lookups/zipcodes/${zipCode}`;
            const getZipCode$ = skipSpinner
              ? this._httpClient.get<ZipcodeModel>(zipcodeEndpoint, { headers: CmgUiUtilities.getSkipSpinnerHeaders() })
              : this._httpClient.get<ZipcodeModel>(zipcodeEndpoint);
            return getZipCode$.pipe(
              map(this._mapZipCodeModel),
              tap((zipCodeModel) => {
                const zipCodeModelsCopy = { ...zipCodeModels };
                zipCodeModelsCopy[zipCodeModel.zipCode] = zipCodeModel;
                this._lookupsStore.update({ zipCodeModels: zipCodeModelsCopy });
              }),
            );
          }
        }),
      );
  }

  private _mapZipCodeModel(zipCodeModel: ZipcodeModel): ZipcodeModel {
    if (zipCodeModel.zipCodeID) {
      zipCodeModel.countryCode = 'US';
    }
    return zipCodeModel;
  }

  initialize() {
    // TODO: call lookupsModel->loadLookups and populate from those values.

    forkJoin([
      this._getLookups(),
      this._getCounties(),
      this._getJrloNames(),
      this._getRetailCompPlans(),
      this.getRetailLeadTypes(),
      this._getLoanStatusItems(),
      this._getLoanReferrals(),
      this._getSecurityProfileRoles(),
      this._getPricing(),
      this._getLicensingAgencies(),
      this._getRelocationRefTypes(),
      this._getAdditionalProdAssistants(),
    ])
      .pipe(
        tap(
          ([
            lookups,
            counties,
            jrloNames,
            retailCompPlans,
            retailLeadTypes,
            loanStatusItems,
            loanReferrals,
            securityProfileRoles,
            pricing,
            licensingAgencies,
            relocationRefTypes,
            additionalProdAssistants,
          ]) => {
            this._lookupsStore.update(<Cmg1003LookupsModel>{
              ...lookups,
              counties,
              jrloNames,
              retailCompPlans,
              retailLeadTypes,
              loanStatusItems,
              loanReferrals,
              securityProfileRoles,
              pricing,
              licensingAgencies,
              relocationRefTypes,
              additionalProdAssistants,
              ...Cmg1003LookupsModel.loadLookups(),
            });
          },
        ),
        take(1),
      )
      .subscribe();
  }
}
